import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaFileExcel, FaTrash, FaEdit, FaSearch, FaTimes, FaChevronDown, FaImage } from "react-icons/fa";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

const XRayList = () => {
  const [xrays, setXrays] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [editingXray, setEditingXray] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    price: "",
    gender: "", // NEW: Added gender field
    preparation: "",
    reportTime: "",
    image: null,
    diagnostics: []
  });

  const [diagnosticsList, setDiagnosticsList] = useState([]);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Get image URL function
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    // If imagePath is already a full URL, return as is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }

    // If imagePath starts with /uploads, add base URL
    if (imagePath.startsWith('/uploads')) {
      // Encode the URL properly (handle spaces and special characters)
      const encodedPath = encodeURI(imagePath);
      return `https://api.elthiumhealth.com${encodedPath}`;
    }

    // If it's just a filename, assume it's in the uploads folder
    return `https://api.elthiumhealth.com/uploads/scans/${encodeURI(imagePath)}`;
  };

  useEffect(() => {
    fetchXrays();
    fetchDiagnostics();
  }, []);

  const fetchXrays = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("authToken");

      const res = await axios.get(
        "https://api.elthiumhealth.com/api/admin/getallxrays",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data) {
        setXrays(res.data || []);
      } else {
        setXrays([]);
      }
    } catch (error) {
      console.error(
        "Failed to fetch X-Rays:",
        error.response?.data || error.message
      );

      setError("Failed to fetch X-Rays. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDiagnostics = async () => {
    try {
      const token = localStorage.getItem("authToken");

      setLoadingDiagnostics(true);

      const response = await axios.get(
        "https://api.elthiumhealth.com/api/admin/alldiagnostics",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.data) {
        setDiagnosticsList(response.data.data);
      } else {
        setDiagnosticsList([]);
      }
    } catch (error) {
      console.error(
        "Error fetching diagnostics:",
        error.response?.data || error.message
      );

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "Failed to load diagnostics list",
      });

      setDiagnosticsList([]);
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this X-Ray record?")) {
      return;
    }

    try {
      const token = localStorage.getItem("authToken");

      const res = await axios.delete(
        "https://api.elthiumhealth.com/api/admin/deletexray",
        {
          data: {
            xrayId: id,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.status === 200) {
        setXrays((prevXrays) =>
          prevXrays.filter((xray) => xray._id !== id)
        );

        setSuccessMessage("X-Ray deleted successfully.");
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (error) {
      console.error(
        "Failed to delete X-Ray:",
        error.response?.data || error.message
      );

      setError(
        error.response?.data?.message || "Failed to delete X-Ray."
      );

      setTimeout(() => setError(""), 3000);
    }
  };

  const handleEditClick = (xray) => {
    setEditingXray(xray);
    setEditForm({
      title: xray.title || "",
      price: xray.price || "",
      gender: xray.gender || "", // NEW: Add gender from xray
      preparation: xray.preparation || "",
      reportTime: xray.reportTime || "",
      image: null,
      diagnostics: Array.isArray(xray.diagnostics)
        ? xray.diagnostics.filter(d => d && d._id).map(d => d._id)
        : []
    });
    setDropdownOpen(false);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: value });
  };

  const handleImageChange = (e) => {
    setEditForm({ ...editForm, image: e.target.files[0] });
  };

  const handleDiagnosticChange = (diagnosticId) => {
    const currentDiagnostics = [...editForm.diagnostics];

    if (currentDiagnostics.includes(diagnosticId)) {
      setEditForm({
        ...editForm,
        diagnostics: currentDiagnostics.filter(id => id !== diagnosticId)
      });
    } else {
      setEditForm({
        ...editForm,
        diagnostics: [...currentDiagnostics, diagnosticId]
      });
    }
  };

  const handleSelectAll = () => {
    if (editForm.diagnostics.length === diagnosticsList.length) {
      setEditForm({ ...editForm, diagnostics: [] });
    } else {
      const allDiagnosticIds = diagnosticsList.map(d => d._id);
      setEditForm({ ...editForm, diagnostics: allDiagnosticIds });
    }
  };

  const handleEditSubmit = async () => {
    try {
      const token = localStorage.getItem("authToken");

      const data = {
        xrayId: editingXray._id,
        title: editForm.title,
        price: editForm.price,
        gender: editForm.gender,
        preparation: editForm.preparation,
        reportTime: editForm.reportTime,
        diagnosticIds: editForm.diagnostics,
      };

      console.log("Sending JSON data:", data);

      const res = await axios.put(
        "https://api.elthiumhealth.com/api/admin/updatexray",
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Response:", res.data);

      if (res.status === 200) {
        setSuccessMessage("X-Ray updated successfully.");
        setEditingXray(null);
        await fetchXrays();
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (error) {
      console.error(
        "Failed to update X-Ray:",
        error.response?.data || error.message
      );

      setError(
        error.response?.data?.message || "Failed to update X-Ray."
      );

      setTimeout(() => setError(""), 3000);
    }
  };

  const formatAssignedDiagnostics = (diagnostics) => {
    if (!Array.isArray(diagnostics) || diagnostics.length === 0) {
      return "-";
    }

    const diagnosticNames = diagnostics
      .filter(diagnostic => diagnostic && diagnostic.name)
      .map(diagnostic => diagnostic.name);

    if (diagnosticNames.length === 0) return "-";

    return diagnosticNames.join(", ");
  };

  const getDiagnosticNameById = (id) => {
    const diagnostic = diagnosticsList.find(d => d._id === id);
    return diagnostic ? diagnostic.name : "Unknown Diagnostic";
  };

  const filteredXrays = xrays.filter((xray) => {
    if (!xray) return false;

    const searchLower = search.toLowerCase();
    return (
      (xray.title && xray.title.toLowerCase().includes(searchLower)) ||
      (xray.gender && xray.gender.toLowerCase().includes(searchLower)) || // NEW: Search by gender
      (xray.preparation && xray.preparation.toLowerCase().includes(searchLower)) ||
      (xray.reportTime && xray.reportTime.toLowerCase().includes(searchLower)) ||
      (xray.price && xray.price.toString().includes(search)) ||
      (xray.diagnostics && xray.diagnostics.some(d =>
        d && d.name && d.name.toLowerCase().includes(searchLower)
      )) ||
      search === ""
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredXrays.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredXrays.length / itemsPerPage);

  const exportToExcel = () => {
    const formatted = xrays.map(({ _id, __v, createdAt, updatedAt, image, ...rest }) => {
      const formattedItem = {};
      for (const key in rest) {
        if (key === 'diagnostics') {
          formattedItem[key] = formatAssignedDiagnostics(rest[key]);
        } else {
          formattedItem[key] = rest[key] === null || rest[key] === undefined ? "N/A" : rest[key];
        }
      }
      return formattedItem;
    });

    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "X-Rays");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(blob, "xrays.xlsx");
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return "N/A";
    return `₹${price}`;
  };

  const formatText = (text) => {
    if (!text) return "-";
    return text.length > 50 ? `${text.substring(0, 50)}...` : text;
  };

  // Format gender with colored badge
  const formatGender = (gender) => {
    if (!gender) return "-";

    let badgeClass = "";
    switch (gender.toLowerCase()) {
      case 'male':
        badgeClass = "bg-blue-100 text-blue-800";
        break;
      case 'female':
        badgeClass = "bg-pink-100 text-pink-800";
        break;
      case 'both':
        badgeClass = "bg-gray-100 text-gray-800";
        break;
      default:
        badgeClass = "bg-gray-100 text-gray-800";
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
        {gender}
      </span>
    );
  };

  // Fixed image error handler
  const handleImageError = (e) => {
    console.log("Image failed to load:", e.target.src);
    e.target.style.display = 'none';
    // Create fallback element
    const parent = e.target.parentNode;
    if (parent && !parent.querySelector('.image-fallback')) {
      const fallback = document.createElement('div');
      fallback.className = 'image-fallback flex flex-col items-center justify-center h-10 w-10 bg-gray-100 rounded';
      fallback.innerHTML = `
        <FaImage class="text-gray-400 text-xs" />
        <span class="text-gray-400 text-[10px] mt-1">No image</span>
      `;
      parent.appendChild(fallback);
    }
  };

  if (loading) {
    return (
      <div className="p-4 bg-white rounded shadow">
        <div className="flex justify-center items-center h-64">
          <div className="text-xl">Loading X-Rays...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white rounded shadow">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">All Scans & X-Rays</h2>
        <span className="text-sm text-gray-500">
          {filteredXrays.length} record(s) found
        </span>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      <div className="mb-4 flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <FaSearch className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            className="pl-10 pr-4 py-2 border rounded text-sm w-full"
            placeholder="Search by title, gender, preparation, report time, price or diagnostic..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
            >
              <FaTimes />
            </button>
          )}
        </div>
        <button
          onClick={exportToExcel}
          className="px-4 py-2 bg-green-500 text-white rounded text-sm flex items-center gap-1"
        >
          <FaFileExcel /> Export to Excel
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border rounded text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="p-2 border">Title</th>
              <th className="p-2 border">Gender</th> {/* NEW: Gender column */}
              <th className="p-2 border">Price</th>
              <th className="p-2 border">Preparation</th>
              <th className="p-2 border">Report Time</th>
              <th className="p-2 border">Assigned Diagnostics</th>
              <th className="p-2 border">Image</th>
              <th className="p-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((xray) => {
                const imageUrl = getImageUrl(xray.image);
                console.log(`X-Ray ID: ${xray._id}, Image Path: ${xray.image}, Full URL: ${imageUrl}`);

                return (
                  <tr key={xray._id} className="hover:bg-gray-50 border-b">
                    <td className="p-2 border">{xray.title || "-"}</td>
                    <td className="p-2 border">{formatGender(xray.gender)}</td>
                    <td className="p-2 border">{formatPrice(xray.price)}</td>
                    <td className="p-2 border" title={xray.preparation}>
                      {formatText(xray.preparation)}
                    </td>
                    <td className="p-2 border">{xray.reportTime || "-"}</td>
                    <td className="p-2 border" title={formatAssignedDiagnostics(xray.diagnostics)}>
                      {formatText(formatAssignedDiagnostics(xray.diagnostics))}
                    </td>
                    <td className="p-2 border">
                      {xray.image ? (
                        <div className="relative h-10 w-10">
                          <img
                            src={imageUrl}
                            alt={xray.title || "X-Ray"}
                            className="h-full w-full object-cover rounded"
                            onError={handleImageError}
                            onLoad={() => console.log(`Image loaded: ${imageUrl}`)}
                          />
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-10 w-10 bg-gray-100 rounded">
                          <FaImage className="text-gray-400 text-xs" />
                          <span className="text-gray-400 text-[10px] mt-1">No image</span>
                        </div>
                      )}
                    </td>
                    <td className="p-2 border flex gap-2 justify-center">
                      <button
                        onClick={() => handleEditClick(xray)}
                        className="text-blue-500 hover:text-blue-700 p-1"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(xray._id)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" className="text-center p-4"> {/* Updated colSpan to 8 */}
                  {search ? "No matching X-Rays found" : "No X-Rays available"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredXrays.length > 0 && (
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded disabled:opacity-50"
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <div className="flex items-center justify-center">
            <span className="text-sm">
              Page {currentPage} of {totalPages} ({filteredXrays.length} items)
            </span>
          </div>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded disabled:opacity-50"
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {editingXray && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-auto">
            <div className="flex justify-between items-center mb-6 pb-4 border-b">
              <h3 className="text-xl font-semibold text-gray-800">Edit X-Ray/Scan</h3>
              <button
                onClick={() => setEditingXray(null)}
                className="text-gray-500 hover:text-gray-700 text-lg"
              >
                <FaTimes />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={editForm.title}
                    onChange={handleEditChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter X-Ray/Scan title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={editForm.price}
                    onChange={handleEditChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter price"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="gender"
                    value={editForm.gender}
                    onChange={handleEditChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report Time
                  </label>
                  <input
                    type="text"
                    name="reportTime"
                    value={editForm.reportTime}
                    onChange={handleEditChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 1 hour, 2 days"
                  />
                </div>
              </div>

              {/* Diagnostics Selection */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-800">Assign Diagnostics Centers</h4>
                  <span className="text-xs text-gray-500">
                    {editForm.diagnostics.length} selected
                  </span>
                </div>

                {loadingDiagnostics ? (
                  <div className="text-center py-4">
                    <div className="text-sm text-gray-500">Loading diagnostics...</div>
                  </div>
                ) : diagnosticsList.length > 0 ? (
                  <div>
                    {/* Dropdown */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <span className="text-gray-700">
                          {editForm.diagnostics.length === 0
                            ? "Choose Diagnostics"
                            : `${editForm.diagnostics.length} diagnostic(s) selected`
                          }
                        </span>
                        <FaChevronDown className={`text-gray-400 transition-transform ${dropdownOpen ? 'transform rotate-180' : ''}`} />
                      </button>

                      {dropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {/* Select All Option */}
                          <div className="p-3 border-b bg-gray-50">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editForm.diagnostics.length === diagnosticsList.length}
                                onChange={handleSelectAll}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm font-medium text-gray-700">
                                {editForm.diagnostics.length === diagnosticsList.length
                                  ? "Deselect All"
                                  : "Select All"
                                }
                              </span>
                            </label>
                          </div>

                          {/* Diagnostics List */}
                          <div className="p-2">
                            {diagnosticsList.map((diagnostic) => (
                              <label
                                key={diagnostic._id}
                                className="flex items-center p-3 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={editForm.diagnostics.includes(diagnostic._id)}
                                  onChange={() => handleDiagnosticChange(diagnostic._id)}
                                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="ml-3 text-sm text-gray-700">
                                  {diagnostic.name || "Unnamed Diagnostic"}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Selected Diagnostics Preview */}
                    {editForm.diagnostics.length > 0 && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <h5 className="font-medium text-blue-800 text-sm mb-2">Selected Diagnostics:</h5>
                        <div className="flex flex-wrap gap-2">
                          {editForm.diagnostics.map(diagnosticId => (
                            <span
                              key={diagnosticId}
                              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium flex items-center gap-1"
                            >
                              {getDiagnosticNameById(diagnosticId)}
                              <button
                                type="button"
                                onClick={() => handleDiagnosticChange(diagnosticId)}
                                className="text-blue-600 hover:text-blue-800 ml-1"
                              >
                                <FaTimes className="text-xs" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-gray-500">
                    No diagnostics centers available
                  </div>
                )}
              </div>

              {/* Preparation Instructions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preparation Instructions
                </label>
                <textarea
                  name="preparation"
                  value={editForm.preparation}
                  onChange={handleEditChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="4"
                  placeholder="Enter preparation instructions"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {editingXray.image && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-1">Current Image:</p>
                    <div className="flex items-center gap-4">
                      <div className="relative h-40 w-40 border rounded-lg overflow-hidden bg-gray-100">
                        <img
                          src={getImageUrl(editingXray.image)}
                          alt={editingXray.title || "X-Ray"}
                          className="h-full w-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const parent = e.target.parentNode;
                            if (parent && !parent.querySelector('.image-fallback')) {
                              const fallback = document.createElement('div');
                              fallback.className = 'image-fallback flex flex-col items-center justify-center h-full w-full';
                              fallback.innerHTML = `
                                <FaImage class="text-gray-400 text-xl" />
                                <span class="text-gray-500 text-sm mt-2">Image not found</span>
                              `;
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      </div>
                      <div className="text-sm text-gray-500">
                        <p className="font-medium">Image Path:</p>
                        <p className="break-all text-xs mt-1">{editingXray.image}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
              <button
                onClick={() => setEditingXray(null)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <FaEdit /> Update X-Ray
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default XRayList;